import Papa from "papaparse";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useTags } from "@app/shared/components/TagListEditor/data";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Button } from "@design-system/Inputs/Button";
import {
    CreateSupplierInput,
    TagType,
    UserRole,
} from "../../../../../../generated/client/graphql";

export const useCsvImport = (
    onAddSuppliers: (suppliers: CreateSupplierInput[]) => void,
) => {
    const { tags } = useTags(TagType.Supplier);
    const onCsvImport = (file: File) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results: any) {
                const csvSuppliers = results.data.map((row: any) => {
                    const rowTags = row.tags
                        ?.split(",")
                        .map((t: string) => t.trim().toLowerCase());
                    const tagIds = tags
                        ?.filter((t) => rowTags?.includes(t.name.toLowerCase()))
                        .map((t) => t.id);
                    const newSupplier: CreateSupplierInput = {
                        name: row.name || "",
                        pointOfContact: {
                            email: row.email || "",
                            firstName: row.firstname || "",
                            lastName: row.lastname || "",
                            role: UserRole.Contributor,
                        },
                        tagIds,
                    };
                    return newSupplier;
                });
                onAddSuppliers(csvSuppliers);
            },
        });
    };

    return onCsvImport;
};

export const ImportCSVButton = ({
    onAddSuppliers,
}: {
    onAddSuppliers: (suppliers: CreateSupplierInput[]) => void;
}) => {
    const { t } = useTranslation("NewSupplierModal");
    const ref = useRef<HTMLInputElement>(null);
    const onCsvImport = useCsvImport(onAddSuppliers);
    const handleImportClick = () => {
        ref.current?.click();
    };

    return (
        <>
            <input
                ref={ref}
                type="file"
                className="hidden"
                onChange={(e) => {
                    e.target.files && onCsvImport(e.target.files[0]);
                    if (ref.current) {
                        ref.current.files = null;
                        ref.current.value = "";
                    }
                }}
            />
            <Tooltip>
                <Tooltip.Trigger asChild>
                    <Button variant="tonal" onClick={handleImportClick}>
                        {t("import_csv")}
                        <Button.Icon name="circleInfo" size="sm" />
                    </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>{t("import_csv_tooltip")}</Tooltip.Content>
            </Tooltip>
        </>
    );
};
